import { useEffect, useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoiceApi, companyApi, type Invoice } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useConfirm } from '../ConfirmProvider';
import { useToast, errMsg } from '../ToastProvider';
import { Button, Card, ErrorNote, PageHeader, StatusBadge, euro } from '../ui';

// Blob im Browser herunterladen.
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function InvoiceDetail(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const { active } = useCompany();
  const confirm = useConfirm();
  const toast = useToast();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [features, setFeatures] = useState<string[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // Versand-Dialog: Mail an den Kunden ist optional und wird vorher bestaetigt.
  const [sendOpen, setSendOpen] = useState(false);
  const [mailTo, setMailTo] = useState('');
  const [mailMessage, setMailMessage] = useState('');
  const [withMail, setWithMail] = useState(true);

  const eRechnung = features.includes('e_rechnung');

  // Kunden-Mailadresse als Vorschlag in den Dialog uebernehmen.
  useEffect(() => {
    const mail = invoice?.customer?.email ?? '';
    setMailTo(mail);
    setWithMail(Boolean(mail));
  }, [invoice?.customer?.email]);

  async function load() {
    if (!active || !id) return;
    try {
      setInvoice(await invoiceApi.get(active.id, id));
    } catch (e) {
      setError(e);
    }
  }
  useEffect(() => {
    void load();
    if (active) companyApi.usage(active.id).then((u) => setFeatures(u.features)).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, id]);

  // Vorschau: PDF inline laden und im Modal anzeigen.
  async function openPreview() {
    if (!active || !id) return;
    setExporting(true);
    try {
      const blob = await invoiceApi.pdf(active.id, id, true);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  }

  function closePreview() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  }

  async function exportAs(kind: 'pdf' | 'zugferd' | 'xrechnung') {
    if (!active || !id || !invoice) return;
    setExporting(true);
    try {
      const num = invoice.number ?? 'rechnung';
      if (kind === 'pdf') downloadBlob(await invoiceApi.pdf(active.id, id), `${num}.pdf`);
      else if (kind === 'zugferd') downloadBlob(await invoiceApi.zugferd(active.id, id), `${num}-zugferd.pdf`);
      else downloadBlob(await invoiceApi.xrechnung(active.id, id), `${num}-xrechnung.xml`);
      toast.success('Export erstellt.');
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  }

  // Korrektur-Dialog (Schreibfehler in einer bereits versendeten Rechnung).
  const [correctOpen, setCorrectOpen] = useState(false);
  // Zahlungsdatum-Dialog: Die EUER folgt dem Zuflussprinzip, also muss der
  // echte Zahltag erfassbar sein — sonst landen nachgetragene Rechnungen
  // alle im laufenden Jahr.
  const [payOpen, setPayOpen] = useState(false);
  const [paySkonto, setPaySkonto] = useState(false);
  const [payDate, setPayDate] = useState('');
  const [corrName, setCorrName] = useState('');
  const [corrAddress, setCorrAddress] = useState('');
  const [corrNotes, setCorrNotes] = useState('');
  const [corrItems, setCorrItems] = useState<{ id: string; description: string }[]>([]);

  // Felder beim Oeffnen aus der Rechnung vorbelegen.
  function openCorrect() {
    if (!invoice) return;
    setCorrName(invoice.recipientName ?? '');
    setCorrAddress(invoice.recipientAddress ?? '');
    setCorrNotes(invoice.notes ?? '');
    // id ist im Typ optional (gleicher Typ beim Anlegen); beim Korrigieren
    // existiert sie immer — Positionen ohne id sind nicht adressierbar.
    setCorrItems(
      (invoice.items ?? [])
        .filter((it): it is typeof it & { id: string } => Boolean(it.id))
        .map((it) => ({ id: it.id, description: it.description })),
    );
    setCorrectOpen(true);
  }

  async function saveCorrection() {
    if (!active || !id) return;
    setBusy(true);
    setError(null);
    try {
      const upd = await invoiceApi.correct(active.id, id, {
        recipientName: corrName.trim(),
        recipientAddress: corrAddress,
        notes: corrNotes.trim() || null,
        items: corrItems,
      });
      setInvoice(upd);
      setCorrectOpen(false);
      toast.success('Rechnung berichtigt. Bitte dem Kunden die korrigierte Fassung zusenden.');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  // Betragsfehler: stornieren und als bearbeitbaren Entwurf neu anlegen.
  async function replaceInvoice() {
    if (!active || !id) return;
    const ok = await confirm({
      title: 'Rechnung ersetzen?',
      message:
        'Die Rechnung wird storniert (inkl. Buchungen) und als neuer Entwurf mit denselben Daten angelegt. Dort kannst du alles ändern, auch Beträge. Die stornierte Rechnung bleibt aus GoBD-Gründen erhalten.',
      confirmLabel: 'Stornieren & neu anlegen',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await invoiceApi.replace(active.id, id);
      toast.success('Storniert. Der neue Entwurf ist offen zum Bearbeiten.');
      navigate(`/app/rechnungen/${res.invoice.id}/bearbeiten`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  // Versenden inkl. optionaler Kundenmail (PDF im Anhang).
  async function doSend() {
    if (!active || !id) return;
    setBusy(true);
    setError(null);
    try {
      const res = await invoiceApi.send(active.id, id, {
        sendMail: withMail,
        ...(withMail && mailTo.trim() ? { to: mailTo.trim() } : {}),
        ...(mailMessage.trim() ? { message: mailMessage.trim() } : {}),
      });
      setInvoice(res.invoice);
      setSendOpen(false);
      if (!withMail) toast.success('Versendet.');
      else if (res.mail?.sent) toast.success(`Versendet und per E-Mail an ${res.mail.to} geschickt.`);
      else toast.error(res.mail?.reason ?? 'Versendet, aber die E-Mail konnte nicht zugestellt werden.');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  /**
   * Oeffnet den Zahlungsdialog.
   *
   * Vorschlag ist heute — ausser bei einer Rechnung aus einem frueheren
   * Jahr: Dort ist "heute" fast immer falsch. Wer eine Rechnung von 2024
   * nachtraegt, hat das Geld damals erhalten, nicht jetzt. Der Erloes
   * zaehlt nach dem Zuflussprinzip in das Jahr der Zahlung — mit "heute"
   * landet er im laufenden Jahr und fehlt in der alten EUER.
   *
   * Deshalb: Bei Altjahren das Faelligkeits- bzw. Rechnungsdatum
   * vorschlagen. Das ist naeher an der Wahrheit als heute, und der Nutzer
   * kann es weiterhin aendern.
   */
  function openPay(withSkonto: boolean) {
    const heute = new Date().toISOString().slice(0, 10);
    const rechnung = invoice?.issueDate ? invoice.issueDate.slice(0, 10) : '';
    const faellig = invoice?.dueDate ? invoice.dueDate.slice(0, 10) : '';
    const ausAltjahr =
      rechnung && Number(rechnung.slice(0, 4)) < new Date().getFullYear();

    let vorschlag = heute;
    if (ausAltjahr) {
      // Faelligkeit ist der beste Schaetzwert; sonst das Rechnungsdatum.
      vorschlag = faellig && faellig >= rechnung ? faellig : rechnung;
    } else if (rechnung && rechnung > heute) {
      // Vordatierte Rechnung: nicht vor dem Rechnungsdatum zahlen.
      vorschlag = rechnung;
    }

    setPaySkonto(withSkonto);
    setPayDate(vorschlag);
    setPayOpen(true);
  }

  async function doPay() {
    if (!payDate) return;
    setPayOpen(false);
    await act('pay', { ...(paySkonto ? { withSkonto: true } : {}), paidAt: payDate });
  }

  async function act(action: 'send' | 'pay' | 'cancel' | 'accept', opts?: { withSkonto?: boolean; paidAt?: string }) {
    if (!active || !id) return;
    setBusy(true);
    setError(null);
    try {
      setInvoice(await invoiceApi.action(active.id, id, action, opts));
      toast.success({ send: 'Versendet.', pay: opts?.withSkonto ? 'Als bezahlt (mit Skonto) markiert.' : 'Als bezahlt markiert.', cancel: 'Storniert.', accept: 'Angebot angenommen.' }[action]);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  // Angebot in eine echte Rechnung umwandeln -> zur neuen Rechnung springen.
  async function convert() {
    if (!active || !id) return;
    setBusy(true);
    setError(null);
    try {
      const rechnung = await invoiceApi.convert(active.id, id);
      toast.success(`Rechnung ${rechnung.number ?? ''} aus Angebot erstellt.`);
      navigate(`/app/rechnungen/${rechnung.id}`, { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (!active || !id) return;
    const ok = await confirm({ title: 'Entwurf löschen?', message: 'Dieser Rechnungsentwurf wird dauerhaft entfernt.', confirmLabel: 'Löschen', danger: true });
    if (!ok) return;
    await invoiceApi.remove(active.id, id);
    toast.info('Entwurf gelöscht.');
    navigate('/app/rechnungen', { replace: true });
  }

  if (!invoice) return <p className="mx-auto max-w-3xl text-gray-500">Lädt …</p>;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title={`${invoice.type === 'quote' ? 'Angebot ' : ''}${invoice.number ?? 'Entwurf'}`}
        subtitle={invoice.recipientName ?? undefined}
        action={<StatusBadge status={invoice.status} />}
      />
      {invoice.type === 'quote' && invoice.validUntil && (
        <p className="mb-3 text-sm text-gray-500">Gültig bis {new Date(invoice.validUntil).toLocaleDateString('de-DE')}</p>
      )}

      <Card className="space-y-4">
        <ErrorNote error={error} />

        {/* Datum prominent: ein falsch gesetztes Rechnungsdatum (z.B. bei der
            Nacherfassung alter Belege) soll sofort auffallen, nicht erst im PDF. */}
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <div>
            <span className="text-xs uppercase tracking-wide text-gray-400">Rechnungsdatum </span>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString("de-DE") : "—"}
            </span>
          </div>
          {invoice.dueDate && (
            <div>
              <span className="text-xs uppercase tracking-wide text-gray-400">Fällig am </span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {new Date(invoice.dueDate).toLocaleDateString("de-DE")}
              </span>
            </div>
          )}
        </div>

        {/* Empfänger-Block inkl. Kundennummer */}
        <div className="rounded-xl bg-gray-50 p-3 text-sm">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">Empfänger</div>
          <div className="font-medium text-gray-900">{invoice.recipientName ?? 'Kein Empfänger'}</div>
          {invoice.customer?.customerNumber && (
            <div className="text-gray-500">Kundennummer: {invoice.customer.customerNumber}</div>
          )}
          {invoice.recipientAddress && (
            <div className="whitespace-pre-line text-gray-500">{invoice.recipientAddress}</div>
          )}
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-gray-500">
              <th className="py-2">Beschreibung</th>
              <th className="py-2 text-right">Menge</th>
              <th className="py-2 text-right">Einzelpreis</th>
              <th className="py-2 text-right">USt</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items?.map((it, i) => (
              <tr key={i} className="border-b border-gray-50">
                <td className="py-2">{it.description}</td>
                <td className="py-2 text-right">{it.quantity}</td>
                <td className="py-2 text-right">{euro(it.unitPriceCents)}</td>
                <td className="py-2 text-right">{Number(it.taxRate)}%</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="space-y-1 border-t border-gray-100 pt-3 text-right text-sm">
          <div className="text-gray-600">Netto: {euro(invoice.netCents)}</div>
          <div className="text-gray-600">USt: {euro(invoice.taxCents)}</div>
          <div className="text-lg font-bold text-gray-900">Gesamt: {euro(invoice.grossCents)}</div>
          {Number(invoice.skontoPercent ?? 0) > 0 && (
            <div className="text-xs text-emerald-600">
              {Number(invoice.skontoPercent)}% Skonto{invoice.skontoDays ? ` (${invoice.skontoDays} Tage)` : ''}: {euro(Math.round(invoice.grossCents * (1 - Number(invoice.skontoPercent) / 100)))}
            </div>
          )}
        </div>

        {/* Export-Bereich: Vorschau + Downloads */}
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
          <Button variant="secondary" onClick={openPreview} busy={exporting}>
            👁 Vorschau
          </Button>
          <Button variant="secondary" onClick={() => exportAs('pdf')} disabled={exporting}>
            PDF herunterladen
          </Button>
          <Button variant="secondary" onClick={() => exportAs('zugferd')} disabled={exporting || !eRechnung}>
            E-Rechnung: ZUGFeRD {!eRechnung && '🔒'}
          </Button>
          <Button variant="secondary" onClick={() => exportAs('xrechnung')} disabled={exporting || !eRechnung}>
            E-Rechnung: XRechnung {!eRechnung && '🔒'}
          </Button>
        </div>
        {!eRechnung && (
          <p className="text-xs text-gray-500">
            🔒 E-Rechnung (ZUGFeRD/XRechnung) ist in deinem Tarif nicht enthalten.{' '}
            <a href="/app/einstellungen/tarif" className="font-medium text-blue-600 hover:underline">Tarif ansehen</a>
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
          {invoice.type === 'quote' ? (
            <>
              {invoice.status === 'draft' && (
                <>
                  <Button variant="danger" onClick={del}>Löschen</Button>
                  <Button variant="secondary" onClick={() => navigate(`/app/rechnungen/${invoice.id}/bearbeiten`)}>Bearbeiten</Button>
                  <Button onClick={() => setSendOpen(true)} busy={busy}>Versenden</Button>
                </>
              )}
              {invoice.status === 'sent' && (
                <>
                  <Button variant="secondary" onClick={() => act('cancel')} busy={busy}>Ablehnen/Stornieren</Button>
                  <Button variant="secondary" onClick={() => act('accept')} busy={busy}>Angenommen</Button>
                </>
              )}
              {/* Umwandeln: solange noch nicht umgewandelt und nicht storniert. */}
              {!invoice.convertedToId && invoice.status !== 'cancelled' && invoice.status !== 'draft' && (
                <Button onClick={convert} busy={busy}>In Rechnung umwandeln</Button>
              )}
              {invoice.convertedToId && (
                <Button variant="secondary" onClick={() => navigate(`/app/rechnungen/${invoice.convertedToId}`)}>Zur Rechnung</Button>
              )}
            </>
          ) : (
            <>
              {invoice.status === 'draft' && (
                <>
                  <Button variant="danger" onClick={del}>Löschen</Button>
                  <Button variant="secondary" onClick={() => navigate(`/app/rechnungen/${invoice.id}/bearbeiten`)}>Bearbeiten</Button>
                  <Button onClick={() => setSendOpen(true)} busy={busy}>Versenden</Button>
                </>
              )}
              {/* Schreibfehler korrigieren: ab versendet, auch bei bezahlt. */}
              {invoice.status !== 'draft' && invoice.status !== 'cancelled' && (
                <Button variant="secondary" onClick={openCorrect}>Korrigieren</Button>
              )}
              {invoice.status === 'sent' && (
                <>
                  <Button variant="secondary" onClick={() => act('cancel')} busy={busy}>Stornieren</Button>
                  {Number(invoice.skontoPercent ?? 0) > 0 && (
                    <Button variant="secondary" onClick={() => openPay(true)} busy={busy}>
                      Bezahlt mit {Number(invoice.skontoPercent)}% Skonto
                    </Button>
                  )}
                  <Button onClick={() => openPay(false)} busy={busy}>Als bezahlt markieren</Button>
                </>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Korrektur-Dialog: Schreibfehler berichtigen (§ 31 Abs. 5 UStDV). */}
      {correctOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && setCorrectOpen(false)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Rechnung korrigieren</h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Schreibfehler in Text und Adresse lassen sich berichtigen. <strong>Beträge, Steuer und Rechnungsnummer
              bleiben unverändert</strong> — sie gehören zur bereits gebuchten Rechnung.
            </p>

            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Empfänger</span>
                <input value={corrName} onChange={(e) => setCorrName(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Adresse</span>
                <textarea value={corrAddress} onChange={(e) => setCorrAddress(e.target.value)} rows={2}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
              </label>

              {corrItems.length > 0 && (
                <div>
                  <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Leistungsbeschreibung</span>
                  <div className="space-y-2">
                    {corrItems.map((it, idx) => (
                      <input key={it.id} value={it.description}
                        onChange={(e) => setCorrItems((prev) => prev.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x)))}
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
                    ))}
                  </div>
                </div>
              )}

              <label className="block">
                <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Notizen</span>
                <textarea value={corrNotes} onChange={(e) => setCorrNotes(e.target.value)} rows={2}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
              </label>
            </div>

            <div className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              Stimmt ein <strong>Betrag</strong> nicht? Dann reicht eine Korrektur nicht aus — die Rechnung muss
              storniert und neu ausgestellt werden.
              <button type="button" onClick={() => { setCorrectOpen(false); replaceInvoice(); }}
                className="ml-1 font-semibold underline">
                Stornieren &amp; neu anlegen
              </button>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setCorrectOpen(false)}>Abbrechen</Button>
              <Button onClick={saveCorrection} busy={busy}>Korrektur speichern</Button>
            </div>
          </div>
        </div>
      )}

      {/* Versand-Dialog: Status setzen + optional Mail an den Kunden. */}
      {sendOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && setSendOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Versenden bestätigen</h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {invoice.type === 'quote' ? 'Das Angebot' : 'Die Rechnung'} wird als versendet markiert
              {invoice.type !== 'quote' ? ' und der Erlös gebucht' : ''}. Danach ist keine Bearbeitung mehr möglich.
            </p>

            <label className="mt-4 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={withMail} onChange={(e) => setWithMail(e.target.checked)} className="mt-0.5 h-4 w-4" />
              <span>Per E-Mail an den Kunden senden (PDF im Anhang)</span>
            </label>

            {withMail && (
              <div className="mt-3 space-y-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">E-Mail-Adresse</span>
                  <input value={mailTo} onChange={(e) => setMailTo(e.target.value)} type="email" placeholder="kunde@example.de"
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
                  {!invoice.customer?.email && (
                    <span className="mt-1 block text-xs text-amber-600 dark:text-amber-400">
                      Beim Kunden ist keine E-Mail hinterlegt — hier eintragen.
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Nachricht (optional)</span>
                  <textarea value={mailMessage} onChange={(e) => setMailMessage(e.target.value)} rows={3}
                    placeholder="z. B. Vielen Dank für Ihren Auftrag."
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
                </label>
                <p className="text-xs text-gray-400">
                  Absender ist noreply@saldify.de im Namen deiner Firma. Antworten gehen an deine Firmen-E-Mail.
                </p>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setSendOpen(false)}>Abbrechen</Button>
              <Button onClick={doSend} busy={busy} disabled={withMail && !mailTo.trim()}>
                {withMail ? 'Versenden & mailen' : 'Nur als versendet markieren'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Zahlungsdatum: entscheidet ueber das Steuerjahr (Zuflussprinzip). */}
      {payOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && setPayOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Als bezahlt markieren</h3>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Wann ist das Geld eingegangen? Dieser Tag entscheidet, in welches Jahr die Einnahme
              zählt (Zuflussprinzip) — nicht der Tag der Erfassung.
            </p>

            <label className="mt-4 block">
              <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Zahlungsdatum</span>
              <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)}
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100" />
            </label>

            {payDate && invoice.issueDate && payDate < invoice.issueDate.slice(0, 10) && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                Das Zahlungsdatum liegt vor dem Rechnungsdatum. Bitte prüfen.
              </p>
            )}
            {/* Jahresgrenze: Der Erloes zaehlt in das Jahr der ZAHLUNG
                (Zuflussprinzip). Liegen Rechnung und Zahlung in
                verschiedenen Jahren, ist das oft ein Versehen — die
                Rechnung wurde nachgetragen und "heute" stehen gelassen. */}
            {payDate && invoice.issueDate &&
              payDate.slice(0, 4) !== invoice.issueDate.slice(0, 4) && (
                <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-950/20">
                  <p className="text-xs text-amber-900 dark:text-amber-200">
                    <strong>
                      Rechnung aus {invoice.issueDate.slice(0, 4)}, Zahlung in {payDate.slice(0, 4)}.
                    </strong>{' '}
                    Der Erlös zählt in das Jahr der Zahlung – er erscheint also in der EÜR
                    {' '}{payDate.slice(0, 4)} und nicht in {invoice.issueDate.slice(0, 4)}.
                    Wenn das Geld damals geflossen ist, trage hier das damalige Datum ein.
                  </p>
                </div>
              )}
            {paySkonto && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Mit {Number(invoice.skontoPercent)}% Skonto (Entgeltminderung nach § 17 UStG).
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPayOpen(false)}>Abbrechen</Button>
              <Button onClick={doPay} busy={busy} disabled={!payDate}>Als bezahlt markieren</Button>
            </div>
          </div>
        </div>
      )}

      {/* Vorschau-Modal: PDF im iframe */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/60 p-2 sm:p-6" onClick={closePreview}>
          <div className="mx-auto flex h-full w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
              <span className="font-medium text-gray-900">Vorschau · {invoice.number}</span>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => exportAs('pdf')} disabled={exporting}>
                  PDF herunterladen
                </Button>
                <Button variant="ghost" onClick={closePreview}>Schließen</Button>
              </div>
            </div>
            <iframe title="Rechnungsvorschau" src={previewUrl} className="h-full w-full flex-1" />
          </div>
        </div>
      )}
    </div>
  );
}
